<script setup lang="ts">
import {computed} from "vue";
import {Clock, Plus, Sun, SunMoon, Sunrise, Sunset, X} from "@lucide/vue";
import {DosageTime, DosageTimeTrigger, DosageTimeWithTime} from "@/dosage/interfaces/Dosage";
import {ProductSummary} from "@/product/interfaces/ProductSummary";
import {ProductDose} from "@/product/interfaces/ProductDose";
import DosageDoseSelect from "@/dosage/components/DosageDoseSelect.vue";
import DosageTimeInput from "@/dosage/components/DosageTimeInput.vue";
import useDosageTimeTrigger from "@/dosage/composables/useDosageTimeTrigger";
import {Button} from "@/core/components/ui/button";

defineProps<{
    product: Pick<ProductSummary, 'id' | 'name'>;
    doseError?: string | null;
    timesError?: string | null;
}>();

const model = defineModel<{
    times: DosageTime[];
    dose: ProductDose | null;
}>({required: true});

const {data: triggers} = useDosageTimeTrigger();

const FIXED_TIMES: { label: string; time: DosageTimeWithTime['time']; icon: unknown }[] = [
    {label: 'Matin', time: '08:00', icon: Sunrise},
    {label: 'Midi', time: '12:00', icon: Sun},
    {label: 'Soir', time: '19:00', icon: Sunset},
    {label: 'Couché', time: '22:00', icon: SunMoon},
];
const FIXED_TIME_VALUES = FIXED_TIMES.map(t => t.time);


const doseForTime = (time: DosageTimeWithTime['time']) => {
    return computed({
        get: () => {
            const entry = model.value.times.find(
                t => !('dosage_time_trigger' in t) && (t as DosageTimeWithTime).time === time
            );
            return entry?.dose ?? 0;
        },
        set: (val: number) => {
            const index = model.value.times.findIndex(
                t => !('dosage_time_trigger' in t) && (t as DosageTimeWithTime).time === time
            );
            if (val > 0 && index === -1) {
                model.value.times.push({dose: val, time});
            } else if (val === 0 && index !== -1) {
                model.value.times.splice(index, 1);
            } else if (index !== -1) {
                model.value.times[index].dose = val;
            }
        },
    });
};


const customTimes = computed(() =>
    model.value.times
        .map((time, index) => ({time, index}))
        .filter(({time}) => !('dosage_time_trigger' in time) && !FIXED_TIME_VALUES.includes((time as DosageTimeWithTime).time))
        .map(({time, index}) => ({entry: time as DosageTimeWithTime, index}))
);

/**
 * Add a new custom take time that is not part of the fixed times or triggers.
 */
const addCustomTime = () => {
    model.value.times.push({dose: 1, time: '13:00'});
};

/**
 * Remove a custom take time by its index in the times array.
 */
const removeCustomTime = (index: number) => {
    model.value.times.splice(index, 1);
};


const triggerTimes = computed(() =>
    model.value.times.filter(time => 'dosage_time_trigger' in time)
);

const isTriggerSelected = (trigger: DosageTimeTrigger) =>
    model.value.times.some(
        t => 'dosage_time_trigger' in t && t.dosage_time_trigger.id === trigger.id
    );

const toggleTrigger = (trigger: DosageTimeTrigger) => {
    if (isTriggerSelected(trigger)) {
        model.value.times = model.value.times.filter(
            time => !('dosage_time_trigger' in time) || time.dosage_time_trigger.id !== trigger.id
        );
    } else {
        model.value.times.push({dose: 1, dosage_time_trigger_id: trigger.id, dosage_time_trigger: trigger});
    }
};

const removeTrigger = (triggerId: number) => {
    model.value.times = model.value.times.filter(
        t => !('dosage_time_trigger' in t) || t.dosage_time_trigger.id !== triggerId
    );
};

const triggerFromSlug = (slug: string) => triggers.value?.find(t => t.slug === slug);
</script>

<template>
  <div class="space-y-3">
    <div>
      <DosageDoseSelect v-model:model-value="model.dose" :product-id="product.id" />
      <p v-if="doseError" class="text-xs text-red-500 mt-1">
        {{ doseError }}
      </p>
    </div>

    <div class="flex flex-wrap gap-2">
      <div
        v-for="slot in FIXED_TIMES"
        :key="slot.time"
        class="flex-1 flex flex-col items-center gap-1 p-2 rounded-lg border border-gray-100 bg-gray-50 transition-colors"
        :class="{'border-primary/30 bg-primary/5': doseForTime(slot.time).value > 0}"
      >
        <component :is="slot.icon" class="size-[18px] text-gray-400" />
        <span class="text-sm text-gray-500">{{ slot.label }}</span>
        <DosageTimeInput v-model="doseForTime(slot.time).value" />
      </div>
    </div>

    <div class="flex flex-row flex-wrap gap-1.5">
      <div
        v-for="{entry, index} in customTimes"
        :key="index"
        class="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700"
      >
        <Clock class="size-3 text-gray-400" />
        <DosageTimeInput v-model="entry.dose" />
        <input
          v-model="entry.time"
          type="time"
          step="1800"
          class="bg-transparent border-0 p-0 text-xs focus:outline-none w-[52px]"
        >
        <button class="text-gray-400 hover:text-gray-600" @click="removeCustomTime(index)">
          <X class="size-3" />
        </button>
      </div>
      <Button
        variant="ghost"
        size="xs"
        class="flex items-center gap-1 text-xs text-gray-400 hover:text-primary transition-colors"
        @click="addCustomTime"
      >
        <Plus class="size-3" />
        Autre heure
      </Button>
    </div>

    <p v-if="timesError" class="text-xs text-red-500 -mt-1">
      {{ timesError }}
    </p>

    <div v-if="triggerTimes.length" class="flex flex-wrap gap-1.5">
      <div
        v-for="t in triggerTimes"
        :key="t.dosage_time_trigger.id"
        class="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-primary/10 text-primary"
      >
        <DosageTimeInput v-model="t.dose" />
        <span>{{ triggerFromSlug(t.dosage_time_trigger.slug)?.name }}</span>
        <button class="text-primary/60 hover:text-primary" @click="removeTrigger(t.dosage_time_trigger.id)">
          <X class="size-3" />
        </button>
      </div>
    </div>

    <div v-if="triggers?.length" class="flex flex-wrap gap-1.5">
      <Button
        v-for="trigger in triggers"
        :key="trigger.id"
        variant="ghost"
        size="xs"
        class="flex items-center gap-1 text-xs transition-colors"
        :class="isTriggerSelected(trigger) ? 'text-primary' : 'text-gray-400 hover:text-primary'"
        @click="toggleTrigger(trigger)"
      >
        <Plus class="size-3" />
        {{ trigger.name }}
      </Button>
    </div>
  </div>
</template>
